/**
 * What to tell someone changing their password. PURE — no DOM.
 *
 * Two jobs: check what was typed BEFORE it is sent, and describe a failure after.
 * The first is not only politeness. Every request counts against a budget of five
 * attempts per ten minutes on the Worker (it guards the current password against
 * guessing), so a typo in the confirm box must not cost one.
 *
 * THE LIMITS BELOW ARE A COPY of src/auth/password-policy.ts. The web and Worker
 * projects share no modules, so the numbers live in both places and
 * test/unit/password-policy.test.ts fails if they ever disagree. The Worker is the
 * authority; this only saves a round trip.
 *
 * Kept out of main.ts for the reason login-errors.ts is: main.ts reads `document`
 * at module load, so importing it inside workerd throws before any assertion runs.
 */

import { ApiError } from './api';

export const MIN_PASSWORD_LENGTH = 15;
export const MAX_PASSWORD_LENGTH = 256;

export const NEW_PASSWORD_HINT = `At least ${MIN_PASSWORD_LENGTH} characters. A few words in a row works well.`;

/** Mirrors NEW_PASSWORD_MESSAGE on the Worker, so a local and a server refusal read alike. */
const TOO_SHORT = `Use at least ${MIN_PASSWORD_LENGTH} characters. A few words in a row works well.`;
const TOO_LONG = `Use at most ${MAX_PASSWORD_LENGTH} characters.`;
const UNCHANGED = 'Choose a different password from the one you have now.';
const MISMATCH = "The two new passwords don't match.";

/**
 * The first problem with what was typed, or null if it is worth sending.
 * Never judges the CURRENT password's strength — only the Worker can say whether
 * it is right, and an older account may legitimately have a shorter one.
 */
export function checkPasswordInput(current: string, next: string, confirm: string): string | null {
  if (!current) return 'Enter your current password.';
  const length = [...next].length;
  if (length < MIN_PASSWORD_LENGTH) return TOO_SHORT;
  if (length > MAX_PASSWORD_LENGTH) return TOO_LONG;
  if (next === current) return UNCHANGED;
  if (next !== confirm) return MISMATCH;
  return null;
}

/**
 * Text for a failed change.
 *
 * The wrong-current-password case is deliberately flat — the same sentence the
 * Worker sends — and does not name which part was wrong.
 */
export function passwordChangeErrorText(err: unknown): string {
  if (!(err instanceof ApiError)) {
    // The request may have reached the Worker and the REPLY been lost, so the
    // password may or may not have changed. Say exactly that, and what to do.
    return "We couldn't confirm whether your password changed. Sign out and sign in with the new one; if that fails, your old one still works.";
  }
  switch (err.status) {
    case 400:
      // Describes the input (too short, unchanged), never the account.
      return err.message;
    case 403:
      return 'Your password was not changed. Check what you entered and try again.';
    case 409:
      return err.message;
    case 429:
      return 'Too many attempts. Try again in a few minutes.';
    case 503:
      return "Ballast can't change passwords right now. This is a server problem, not your password.";
    default:
      return err.message || 'Something went wrong. Your password was not changed.';
  }
}

/**
 * Said after a successful change. The count is how many OTHER devices were signed
 * out — zero is a normal, good answer (nothing else was signed in), not an error.
 */
export function passwordChangedText(otherSessionsRevoked: number): string {
  if (otherSessionsRevoked <= 0) return 'Password changed.';
  const devices = otherSessionsRevoked === 1 ? 'device was' : 'devices were';
  return `Password changed. ${otherSessionsRevoked} other ${devices} signed out.`;
}
